'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Swal from 'sweetalert2';
import type { Country } from 'react-phone-number-input';
import en from 'react-phone-number-input/locale/en.json';
import { authClient } from '@bip/api-client';
import { AccountShell } from '../hotels/_components/AccountShell';
import { LoginGate } from '../hotels/_components/LoginGate';
import { PROXY_PREFIX } from '../hotels/config';
import { CountrySelect } from '../_components/CountrySelect';
import { PhoneInput, isPhoneComplete } from '../_components/PhoneInput';
import { ProfileFormSkeleton } from '../_components/Skeleton';
import '../styles/my-profile.css';

const HOME_HREF = `${PROXY_PREFIX}/hotels`;

// Exact wording from the reference `formValidation.js` `checkInvalidRequired` — every required
// field falls through its `switch(uiDataType)` to the same message (only `dropdown`/`file`-family
// types get a different one, none of which this form uses). Do not invent per-field messages.
const REQUIRED_MSG = 'Please fill out this field.';
// Exact wording from `validPhone` in the reference (no trailing period — distinct from the unused
// `checkInvalidTypeMismatch` phone message, which the reference never reaches since the custom
// `data-custom-validation="validPhone"` check runs first and returns early).
const PHONE_INVALID_MSG = 'Please enter a valid phone number';

// Bootstrap-style plain rows (`my-profile.html`'s `col-sm-3 col-form-label` / `col-sm-9`) —
// label LEFT + input RIGHT, not stacked. Label column is a fixed width so every row's input
// starts at the same X position regardless of label text length.
const rowCls = 'grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-x-4 gap-y-1.5 sm:items-center';
// `!` (important) — globals.css `label { font-size: var(--text-md) }` is unlayered and beats any
// `@layer utilities` class regardless of specificity (see CLAUDE.md Tailwind-cascade gotchas).
const labelCls = 'text-[0.9375rem]! font-semibold text-secondary';
const errorCls = 'text-xs text-red-600 mt-1 sm:col-start-2';

// Square corners (no border-radius at all — the reference has sharp-cornered inputs) + light-grey
// border — Bootstrap `.form-control` border colour (`#ced4da`), NOT this project's pill-ish
// `rounded-xl`/theme border-light (`#f0f0f0`, too pale to read as a visible border in the
// reference screenshot).
// No `bg-*` here — callers append their own (email needs grey, not white) so there's only ever
// ONE background utility in the final class string; two same-property utility classes in one
// string is exactly the kind of conflict that silently picked the wrong one last time (`bg-white`
// from this constant beat the caller's `bg-gray-100`). `rounded-none!` (important) defends against
// any not-yet-found unlayered rule the same way the `h1`/`label` font-size overrides did.
const inputCls =
  'w-full border border-[#ced4da] rounded-none! px-3 py-2.5 text-[0.975rem] outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 transition-colors';
const inputBase = 'w-full border rounded-none! px-3 py-2.5 text-[0.975rem] bg-white outline-none focus:ring-1 transition-colors';
type FieldVisualState = 'error' | 'valid' | 'none';
const borderClsFor = (s: FieldVisualState) =>
  s === 'error'
    ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
    : s === 'valid'
      ? 'border-green-500 focus:border-green-500 focus:ring-green-500/20'
      : 'border-[#ced4da] focus:border-primary focus:ring-primary/20';
function FieldIcon({ state }: { state: FieldVisualState }) {
  if (state === 'none') return null;
  const cls = state === 'error' ? 'fa-circle-exclamation text-red-500' : 'fa-check text-green-600';
  return (
    <i
      className={`fa-solid ${cls} absolute right-3 top-1/2 -translate-y-1/2 text-sm pointer-events-none`}
      aria-hidden="true"
    />
  );
}

// Reference `nonNumericInputs`/`.numeric-input`/VAT filters (`editProfile.js`) — mutate the DOM
// value directly then re-dispatch `input` so React/RHF pick up the filtered value. The `!==` guard
// stops the re-dispatched event from recursing.
function stripDigits(e: React.FormEvent<HTMLInputElement>) {
  const input = e.currentTarget;
  const stripped = input.value.replace(/[0-9]/g, '');
  if (stripped !== input.value) {
    const pos = input.selectionStart ?? stripped.length;
    input.value = stripped;
    input.setSelectionRange(pos, pos);
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }
}
function filterAlphanumeric(e: React.FormEvent<HTMLInputElement>) {
  const input = e.currentTarget;
  const filtered = input.value.replace(/[^a-zA-Z0-9]/g, '');
  if (filtered !== input.value) {
    const pos = input.selectionStart ?? filtered.length;
    input.value = filtered;
    input.setSelectionRange(pos, pos);
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }
}
// Reference `validateCharacterLimit` — silently truncates at 250 chars, no error message.
function clampLength(e: React.FormEvent<HTMLInputElement>, max: number) {
  const input = e.currentTarget;
  if (input.value.length > max) {
    input.value = input.value.slice(0, max);
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }
}

const profileSchema = z.object({
  firstName: z.string().trim().min(1, REQUIRED_MSG),
  lastName: z.string().trim().min(1, REQUIRED_MSG),
  phone: z
    .string()
    .trim()
    .min(1, REQUIRED_MSG)
    .refine((v) => isPhoneComplete(v), { message: PHONE_INVALID_MSG }),
  address1: z.string().trim().min(1, REQUIRED_MSG),
  address2: z.string().optional().default(''),
  city: z.string().trim().min(1, REQUIRED_MSG),
  countryCode: z.string().trim().min(1, REQUIRED_MSG),
  companyName: z.string().optional().default(''),
  vatNumber: z.string().optional().default(''),
});
type ProfileValues = z.infer<typeof profileSchema>;

export default function MyProfilePage() {
  const router = useRouter();
  const [auth, setAuth] = useState<'loading' | 'in' | 'out'>('loading');
  const [email, setEmail] = useState('');
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ProfileValues>({
    resolver: zodResolver(profileSchema) as any,
    defaultValues: {
      firstName: '', lastName: '', phone: '', address1: '', address2: '',
      city: '', countryCode: '', companyName: '', vatNumber: '',
    },
  });

  // Green tick / red icon per field — matches the moment the reference shows it: right after a
  // successful save (the SweetAlert + green ticks appear together), not live while typing.
  const stateOf = (name: keyof ProfileValues): FieldVisualState =>
    errors[name] ? 'error' : saved ? 'valid' : 'none';

  // Auth-gate + prefill from the saved profile (CMS get-contact-by-id projection).
  useEffect(() => {
    let cancelled = false;
    authClient
      .getMe()
      .then((me) => {
        if (cancelled) return;
        if (me.message !== 'success') {
          setAuth('out'); // CMS parity: pop the login modal (LoginGate) instead of bouncing home.
          return;
        }
        setAuth('in');
        return authClient.getContact().then((res) => {
          if (cancelled) return;
          const c = res.data || {};
          setEmail(c.Email || me.data?.email || '');
          reset({
            firstName: c.firstName || '',
            lastName: c.lastName || '',
            phone: c.phone || '',
            address1: c.address1 || '',
            address2: c.address2 || '',
            city: c.city || '',
            countryCode: c.countryCode || '',
            companyName: c.companyName || '',
            vatNumber: c.vatNumber || '',
          });
        });
      })
      .catch(() => {
        if (!cancelled) setAuth('out');
      });
    return () => {
      cancelled = true;
    };
  }, [reset, router]);

  const onSubmit = async (values: ProfileValues) => {
    setSaved(false);
    setSaveError('');
    // CMS stores the display country name AND the ISO countryCode; derive the name from the code.
    const countryName = values.countryCode
      ? ((en as Record<string, string>)[values.countryCode] ?? '')
      : '';
    try {
      const res = await authClient.updatePersonalInformation({
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone || '',
        address1: values.address1 || '',
        address2: values.address2 || '',
        city: values.city || '',
        countryCode: values.countryCode || '',
        country: countryName,
        companyName: values.companyName || '',
        vatNumber: values.vatNumber || '',
      });
      if (res.message === 'success' || res.message === 'No changes made') {
        setSaved(true);
        // Exact port of `editProfile.js`'s `Swal.fire({...})` call — same library (sweetalert2)
        // this codebase already uses elsewhere (see `LoginModal.tsx`), same params, so it looks
        // and animates exactly like every other SweetAlert popup in the app.
        Swal.fire({
          text: 'Your personal details have been updated',
          icon: 'success',
          showConfirmButton: false,
          timer: 3000,
        });
      } else {
        setSaveError(res.displayMessage || 'Could not save your changes. Please try again.');
      }
    } catch {
      setSaveError('Could not save your changes. Please try again.');
    }
  };

  if (auth === 'out') {
    return <LoginGate onHome={() => router.replace(HOME_HREF)} />;
  }
  if (auth !== 'in') {
    return <ProfileFormSkeleton />;
  }

  return (
    <AccountShell crumb="Personal Details" breadcrumbVariant="dark-empty" pageBgClassName="bg-[#F5F5F5]">
      <div className="bg-white rounded-2xl p-6 lg:p-8 max-w-2xl">
        {/* `<h2>`, not `<h1>` — globals.css's unlayered `h2, h3, h4, h5, h6 { font-weight:
            var(--font-medium); font-family: var(--secondary-font) }` rule (line 394) then applies
            weight 500 + the "owners" secondary font natively, no extra classes needed for those
            two properties. `text-[1.625rem]!` (important) is still required for the size — the
            same unlayered rule doesn't set font-size, but `@layer utilities` classes never beat
            ANY unlayered rule regardless of which properties it happens to set (this project's
            CLAUDE.md Tailwind-cascade gotchas) — worth keeping `!` here defensively. */}
        <h2 className="text-[1.625rem]! text-secondary mb-1">Personal Details</h2>
        <p className="text-[0.9375rem] text-gray-text mb-4">Update your information and find out how it&apos;s used.</p>
        <hr className="border-t border-[#dee2e6] mb-5" />

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          {/* Name — ONE label, two inputs side by side in the input column (matches the
              reference's single `<label for="Name">` + nested `.row > .col + .col`). */}
          <div className={rowCls}>
            <label htmlFor="firstName" className={labelCls}>
              Name <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div className="relative">
                <input
                  id="firstName"
                  type="text"
                  placeholder="First Name"
                  aria-required="true"
                  aria-invalid={!!errors.firstName}
                  {...register('firstName')}
                  onInput={stripDigits}
                  className={`${inputBase} ${borderClsFor(stateOf('firstName'))} pr-9`}
                />
                <FieldIcon state={stateOf('firstName')} />
              </div>
              <div className="relative">
                <input
                  id="lastName"
                  type="text"
                  placeholder="Last Name"
                  aria-required="true"
                  aria-invalid={!!errors.lastName}
                  {...register('lastName')}
                  onInput={stripDigits}
                  className={`${inputBase} ${borderClsFor(stateOf('lastName'))} pr-9`}
                />
                <FieldIcon state={stateOf('lastName')} />
              </div>
            </div>
            {errors.firstName && <p className={errorCls}>{errors.firstName.message}</p>}
            {!errors.firstName && errors.lastName && <p className={errorCls}>{errors.lastName.message}</p>}
          </div>

          {/* Email (read-only) — light-grey disabled background, Bootstrap `.form-control:disabled`. */}
          <div className={rowCls}>
            <label className={labelCls}>
              Email address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={email}
              disabled
              className={`${inputCls} bg-gray-100! text-black cursor-not-allowed`}
            />
          </div>

          {/* Phone */}
          <div className={rowCls}>
            <label htmlFor="phone" className={labelCls}>
              Phone <span className="text-red-500">*</span>
            </label>
            <div>
              <Controller
                control={control}
                name="phone"
                render={({ field }) => {
                  const phoneState = stateOf('phone');
                  const borderCls =
                    phoneState === 'error'
                      ? 'border-red-300'
                      : phoneState === 'valid'
                        ? 'border-green-500'
                        : 'border-[#ced4da]';
                  return (
                    <PhoneInput
                      id="phone"
                      name={field.name}
                      value={field.value || ''}
                      onChange={field.onChange}
                      defaultCountry={'GB' as Country}
                      required
                      hasError={!!errors.phone}
                      className={`bip-phone profile-phone-field ${phoneState === 'valid' ? 'is-valid' : ''} flex items-stretch border rounded-none bg-white transition-colors ${borderCls}`}
                    />
                  );
                }}
              />
            </div>
            {errors.phone && <p className={errorCls}>{errors.phone.message}</p>}
          </div>

          {/* Address */}
          <div className={rowCls}>
            <label htmlFor="address1" className={labelCls}>
              Your Address <span className="text-red-500">*</span>
            </label>
            <div>
              <div className="relative">
                <input
                  id="address1"
                  type="text"
                  placeholder="Address Line 1"
                  maxLength={250}
                  aria-required="true"
                  aria-invalid={!!errors.address1}
                  {...register('address1')}
                  onInput={(e) => clampLength(e, 250)}
                  className={`${inputBase} ${borderClsFor(stateOf('address1'))} pr-9`}
                />
                <FieldIcon state={stateOf('address1')} />
              </div>
              <div className="relative mt-3">
                <input
                  id="address2"
                  type="text"
                  placeholder="Address Line 2"
                  maxLength={250}
                  {...register('address2')}
                  onInput={(e) => clampLength(e, 250)}
                  className={`${inputBase} ${borderClsFor(stateOf('address2'))} pr-9`}
                />
                <FieldIcon state={stateOf('address2')} />
              </div>
            </div>
            {errors.address1 && <p className={errorCls}>{errors.address1.message}</p>}
          </div>

          {/* City */}
          <div className={rowCls}>
            <label htmlFor="city" className={labelCls}>
              City <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                id="city"
                type="text"
                aria-required="true"
                aria-invalid={!!errors.city}
                {...register('city')}
                onInput={stripDigits}
                className={`${inputBase} ${borderClsFor(stateOf('city'))} pr-9`}
              />
              <FieldIcon state={stateOf('city')} />
            </div>
            {errors.city && <p className={errorCls}>{errors.city.message}</p>}
          </div>

          {/* Country */}
          <div className={rowCls}>
            <label htmlFor="country" className={labelCls}>
              Country <span className="text-red-500">*</span>
            </label>
            <div>
              <Controller
                control={control}
                name="countryCode"
                render={({ field }) => (
                  <CountrySelect
                    id="country"
                    value={field.value || ''}
                    onChange={field.onChange}
                    required
                    hasError={!!errors.countryCode}
                    className={`profile-country-field ${stateOf('countryCode') === 'valid' ? 'is-valid' : ''}`}
                  />
                )}
              />
            </div>
            {errors.countryCode && <p className={errorCls}>{errors.countryCode.message}</p>}
          </div>

          {/* Company Name */}
          <div className={rowCls}>
            <label htmlFor="companyName" className={labelCls}>Company Name</label>
            <div className="relative">
              <input
                id="companyName"
                type="text"
                {...register('companyName')}
                className={`${inputBase} ${borderClsFor(stateOf('companyName'))} pr-9`}
              />
              <FieldIcon state={stateOf('companyName')} />
            </div>
          </div>

          {/* VAT Number */}
          <div className={rowCls}>
            <label htmlFor="vatNumber" className={labelCls}>Vat Number</label>
            <div className="relative">
              <input
                id="vatNumber"
                type="text"
                {...register('vatNumber')}
                onInput={filterAlphanumeric}
                className={`${inputBase} ${borderClsFor(stateOf('vatNumber'))} pr-9`}
              />
              <FieldIcon state={stateOf('vatNumber')} />
            </div>
          </div>

          {saveError && <p className={errorCls} role="alert">{saveError}</p>}

          {/* Save — right-aligned where the inputs end, solid pill button. */}
          <div className="text-right pt-2">
            <button type="submit" disabled={isSubmitting} className="btn-brand px-10 py-2.5 text-sm disabled:opacity-60">
              {isSubmitting ? (
                <span className="inline-flex items-center justify-center gap-2">
                  <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving…
                </span>
              ) : (
                'Save'
              )}
            </button>
          </div>
        </form>

        {/* Full-page blur while the save request is in flight — the success/failure popup
            (Swal, below) only fires once it resolves. */}
        {isSubmitting && (
          <div
            className="fixed inset-0 z-[70] flex items-center justify-center"
            style={{ background: 'rgba(255,255,255,0.6)' }}
            aria-live="polite"
            aria-busy="true"
          >
            <span className="w-6 h-6 border-2 border-black border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>
    </AccountShell>
  );
}
